<script setup lang="ts">
import { createErrorMessage } from '@errors/helpers';
import { useBreadcrumbStore } from '@menu-lib/breadcrumb/breadcrumbStore';
import { getSurenetUrl } from '@menu-lib/urlHelper';
import { useRelatiesGetRelationByRelatienummer } from '@services/crm-public/orval-api/relaties-api/relaties-api';
import FadeSlideTransition from '@shared/animation/transitions/FadeSlideTransition/FadeSlideTransition.vue';
import VInfoBox from '@shared/component/VInfoBox/VInfoBox.vue';
import VSizeContainer from '@shared/layout/VSizeContainer/VSizeContainer.vue';
import { FontAwesomeIcon } from '@shared/plugin/icons';
import { computed, ref, toRefs, watch } from 'vue';
import { useRoute } from 'vue-router';
import { type RouteRecord } from '../types';
import RelatiegegevensMenu from './RelatiegegevensMenu.vue';

const props = defineProps<{
  relatienummer: number;
  routes: RouteRecord[];
}>();

const { relatienummer } = toRefs(props);
const showMobileMenu = ref(false);

const currentRoute = useRoute();
const containerSize = computed(() =>
  (currentRoute?.meta?.extraWidth as boolean) ? 'deprecated_1600' : 'sm',
);

const tabName = computed(
  () =>
    `${currentRoute?.meta?.label}${currentRoute?.params?.polisnummer ? ` ${currentRoute?.params?.polisnummer}` : ''}`,
);

const {
  isPending: relatiePending,
  data: relatienaam,
  error,
  isError,
} = useRelatiesGetRelationByRelatienummer(relatienummer, {
  query: { select: (response) => response?.result?.name },
});

const breadcrumbStore = useBreadcrumbStore();

const breadcrumbs = computed(() => {
  const url = '/app/crm/relatiegegevens';
  const base = { name: 'Relaties', link: getSurenetUrl(url, false) };
  const relatie = { name: `${relatienaam.value || ''} (#${relatienummer.value})`, link: '' };
  const tab = { name: tabName.value || '', link: '' };

  if (relatiePending.value || !relatienaam.value) return [base];
  if (!tabName.value) return [base, relatie];
  return [base, relatie, tab] as Array<{ name: string; link: string }>;
});

watch(breadcrumbs, (b) => (breadcrumbStore.breadcrumbs = b));

const toggleMobileMenu = () => {
  showMobileMenu.value = !showMobileMenu.value;
};

watch(currentRoute, () => {
  showMobileMenu.value = false;
});
</script>

<template>
  <div v-if="isError || Number.isNaN(props.relatienummer)" class="p-4">
    <VInfoBox badge-text="error" type="danger">
      <div v-if="Number.isNaN(props.relatienummer)">Het opgegeven relatienummer is ongeldig.</div>
      <div
        v-if="isError"
        v-html="
          createErrorMessage(error, { onNotFoundError: `De opgegeven relatie bestaat niet.` })
        "
      ></div>
    </VInfoBox>
  </div>
  <div class="flex h-full flex-col" v-else-if="!relatiePending">
    <div class="relative flex grow flex-col xl:flex-row">
      <!-- click spot for mobile  -->
      <div
        class="sticky top-[var(--header-height)] z-dialog h-[var(--subheader-height)] w-full border-0 border-b border-solid border-grey-200 bg-grey-100 xl:h-[calc(100dvh-var(--header-height))] xl:max-w-64 3xl:max-w-[var(--menu-container-width)]"
      >
        <div class="py-2 xl:hidden" @click="toggleMobileMenu">
          <VSizeContainer :size="containerSize">
            <div class="flex w-full flex-row items-center gap-2 py-2 text-sm font-bold xl:w-64">
              <FontAwesomeIcon
                v-if="currentRoute.meta.icon"
                class="shrink-0 [--fa-fw-width:16px]"
                :class="{ hidden: showMobileMenu }"
                fixed-width
                :icon="`fas ${currentRoute.meta.icon}`"
              />
              <span :class="{ hidden: showMobileMenu }">{{ currentRoute.meta.label }}</span>
              <div class="ml-auto shrink-0 px-2">
                <FontAwesomeIcon
                  v-if="currentRoute.meta.icon"
                  class="[--fa-fw-width:24px]"
                  fixed-width
                  :icon="['fas', showMobileMenu ? 'chevron-up' : 'chevron-down']"
                />
              </div>
            </div>
          </VSizeContainer>
        </div>
        <!-- menu container -->
        <div
          class="absolute mt-[1px] w-full overflow-auto bg-grey-100 transition-all duration-300 xl:mt-0"
          :class="{
            'max-xl:h-[calc(100dvh-var(--header-height)-54px)]': showMobileMenu,
            'max-xl:h-0': !showMobileMenu,
          }"
        >
          <RelatiegegevensMenu
            :class="{ 'hidden xl:flex': !showMobileMenu }"
            :relatienummer="relatienummer"
            :routes="props.routes"
          />
        </div>
      </div>
      <!-- page container -->
      <RouterView v-slot="{ Component }" v-if="!relatiePending">
        <FadeSlideTransition direction="down" mode="out-in">
          <KeepAlive>
            <component :is="Component" />
          </KeepAlive>
        </FadeSlideTransition>
      </RouterView>
    </div>
  </div>
</template>
